import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import * as api from "../api/client";
import type { ApiCtx } from "../api/client";
import { useSettings } from "../state/settings";
import { useWorkspaces } from "../state/workspaces";
import { useUi } from "../state/ui";
import { tabsForView, texte, TAB_LABEL } from "../texte";
import { Badge } from "./parts/Badge";
import { Meter } from "./parts/Meter";
import { ShareDialog, VoucherDialog } from "./parts/Dialogs";
import { Feed } from "./tabs/Feed";
import { Files } from "./tabs/Files";
import { Decisions } from "./tabs/Decisions";
import { Plan } from "./tabs/Plan";
import { Config } from "./tabs/Config";
import { Logs } from "./tabs/Logs";
import { Llm } from "./tabs/Llm";
import { Critiques } from "./tabs/Critiques";
import { Search } from "./tabs/Search";
import { RawStatus } from "./tabs/RawStatus";
import {
  combineState,
  deriveStateFromStatus,
  useWorkspaceDecisionsQuery,
  useWorkspaceStatusQuery,
} from "../poll/engine";
import { toast } from "../notify";

export function Workspace() {
  const { token = "", workspace = "" } = useParams<{ token: string; workspace: string }>();
  const apiUrl = useSettings((s) => s.apiUrl);
  const pollSeconds = 5;
  const entry = useWorkspaces((s) => s.byName[workspace]);
  const queryClient = useQueryClient();
  const tab = useUi((s) => s.tab);
  const setTab = useUi((s) => s.setTab);
  const [shareOpen, setShareOpen] = useState(false);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [maxTurns, setMaxTurns] = useState(10);

  const ctx: ApiCtx = { apiUrl, token, workspace };

  const statusQuery = useWorkspaceStatusQuery(ctx, pollSeconds);
  const isRunActive = statusQuery.data?.run?.state === "queued" || statusQuery.data?.run?.state === "running";
  const decisionsQuery = useWorkspaceDecisionsQuery(ctx, isRunActive || statusQuery.data?.run?.state === undefined);
  const planDocQuery = useQuery({
    queryKey: ["doc", apiUrl, token, workspace, "plan"],
    queryFn: () => api.get(ctx, "plan"),
  });
  const configQuery = useQuery({
    queryKey: ["config", apiUrl, token, workspace],
    queryFn: () => api.configure(ctx, {}),
  });
  /** Best-effort: manche Workspaces legen die ursprüngliche Aufgabe als Dokument "goal" ab
   * (vgl. example/*​/files/goal.md). Kein eigener API-Endpunkt dafür — schlägt der Aufruf
   * fehl, bleibt die Aufgaben-Karte im Verlauf einfach weg. */
  const goalQuery = useQuery({
    queryKey: ["doc", apiUrl, token, workspace, "goal"],
    queryFn: () => api.get(ctx, "goal"),
    retry: false,
  });

  if (!entry) return null; // App.tsx registriert unbekannte Tokens, bevor diese Route erreicht wird

  const decisions = decisionsQuery.data?.decisions ?? [];
  const hasOpenDecision = decisions.some((d) => !d.answer);
  const parsed = statusQuery.parsed ?? { agents: [], history: [] };
  const baseState = statusQuery.data ? deriveStateFromStatus(statusQuery.data, parsed) : "ready";
  const state = isSubmitting ? "running" : combineState(baseState, hasOpenDecision, false);
  const budget = {
    total: statusQuery.data?.budget?.total ?? entry.lastKnown?.budget?.total ?? 0,
    used: statusQuery.data?.budget?.used ?? entry.lastKnown?.budget?.used ?? 0,
  };
  // Ist gerade wieder Budget übrig (z. B. nach einem Gutschein), gelten frühere
  // "Budget erschöpft"-Einträge im Verlauf als erledigt — siehe Feed.tsx.
  const budgetHasRoom = budget.total > 0 && budget.used < budget.total;

  const isAdmin = entry.role === "admin";
  const tabs = tabsForView(entry.view);
  const activeTab = tabs.includes(tab) ? tab : "verlauf";

  const config = configQuery.data?.config;
  const hasPlanDoc = !!planDocQuery.data?.content;
  const canPlan = config?.plan === true && !isRunActive && !hasPlanDoc;
  const isRunning = state === "running";

  async function handleAnswer(qid: string, text: string) {
    const res = await api.answer(ctx, qid, text);
    if (res.code < 0) {
      toast(texte.errors.generic, res.msg ?? texte.errors.generic);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["decisions", apiUrl, token, workspace] });
  }

  async function handleStart() {
    setIsSubmitting(true);
    const res = await api.start(ctx, undefined, undefined, maxTurns);
    if (res.code < 0 && res.code !== -2) {
      toast(texte.errors.generic, res.msg ?? texte.errors.generic);
    }
    queryClient.invalidateQueries({ queryKey: ["status", apiUrl, token, workspace] });
    setIsSubmitting(false);
  }

  async function handleStop() {
    setIsSubmitting(true);
    const res = await api.stop(ctx);
    if (res.code < 0) {
      toast(texte.errors.generic, res.msg ?? texte.errors.generic);
    }
    queryClient.invalidateQueries({ queryKey: ["status", apiUrl, token, workspace] });
    setIsSubmitting(false);
  }

  async function handleMakePlan() {
    const res = await api.plan(ctx);
    if (res.code < 0) {
      toast(texte.errors.generic, res.msg ?? texte.errors.generic);
    }
    queryClient.invalidateQueries({ queryKey: ["doc", apiUrl, token, workspace, "plan"] });
    queryClient.invalidateQueries({ queryKey: ["status", apiUrl, token, workspace] });
  }

  async function handleVoucher(amount: number) {
    const res = await api.voucher(ctx, amount);
    if (res.code < 0) {
      toast(texte.errors.generic, res.msg ?? texte.errors.generic);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["status", apiUrl, token, workspace] });
  }

  const shareLink = `${location.origin}${location.pathname}#/ws/${token}/${encodeURIComponent(workspace)}${
    entry.expertEnabled ? "?expert=1" : ""
  }`;

  // File preview lives inside the Dateien tab (list click and rail tree share openFile).
  // Tabs stay visible so "‹ Zur Übersicht" returns to the list without losing the tab bar.
  const center: ReactNode = (
    <>
      <div className="tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t}
            role="tab"
            className={activeTab === t ? "on" : ""}
            onClick={() => {
              setTab(t);
              queryClient.invalidateQueries({ predicate: (q) => q.queryKey.includes(workspace) });
            }}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>
      {activeTab === "verlauf" && (
        <Feed
          task={goalQuery.data?.code === 0 ? goalQuery.data.content : undefined}
          history={parsed.history}
          decisions={decisions}
          running={isRunActive}
          isAdmin={isAdmin}
          budgetHasRoom={budgetHasRoom}
          onAnswer={handleAnswer}
          onOpenVoucher={() => setVoucherOpen(true)}
        />
      )}
      {activeTab === "dateien" && <Files ctx={ctx} isAdmin={isAdmin} />}
      {activeTab === "rueckfragen" && <Decisions decisions={decisions} onAnswer={handleAnswer} />}
      {activeTab === "plan" && <Plan ctx={ctx} planMode={config?.plan === true} showRawFallback={entry.view !== "simple"} />}
      {activeTab === "konfig" && <Config ctx={ctx} />}
      {activeTab === "logs" && <Logs ctx={ctx} history={parsed.history} />}
      {activeTab === "llm" && <Llm ctx={ctx} running={isRunActive} pollSeconds={pollSeconds} />}
      {activeTab === "kritiken" && <Critiques ctx={ctx} critiqueEnabled={config?.critique === true} />}
      {activeTab === "suche" && <Search ctx={ctx} />}
      {activeTab === "rohstatus" && statusQuery.data && <RawStatus status={statusQuery.data} decisions={decisions} />}
    </>
  );

  return (
    <div style={{ minWidth: 0 }}>
      <div className="ws-shell">
        <main className="ws-main">
          <div className="ws-head">
            <h1>{workspace}</h1>
            <Badge state={state} />
            <div className="actions">
              {canPlan && (
                <button className="btn" onClick={handleMakePlan}>
                  {texte.workspace.createPlan}
                </button>
              )}
              {isRunActive ? (
                <button className="btn primary" disabled={isSubmitting} onClick={handleStop}>
                  {texte.workspace.stop}
                </button>
              ) : (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <button className="btn primary" disabled={isSubmitting} onClick={handleStart}>
                    {texte.workspace.start}
                  </button>
                  <label style={{ fontSize: "12px", color: "var(--c-text-sec)", display: "flex", alignItems: "center", gap: "4px" }}>
                    {texte.workspace.maxTurns}:
                    <input
                      type="number"
                      className="input"
                      value={maxTurns}
                      onChange={(e) => setMaxTurns(Number(e.target.value))}
                      style={{ width: "50px", padding: "2px 4px" }}
                      min={1}
                    />
                  </label>
                </div>
              )}
              {isAdmin && (
                <button className="btn" onClick={() => setShareOpen(true)}>
                  {texte.workspace.shareCode}
                </button>
              )}
              {isAdmin && (
                <button className="btn" onClick={() => setVoucherOpen(true)}>
                  {texte.workspace.voucher}
                </button>
              )}
              <button className="btn ghost" title={texte.workspace.reload} onClick={() => statusQuery.refetch()}>
                ⟳
              </button>
            </div>
          </div>
          <div className="budget-line">
            <span className="label">{texte.workspace.budgetLabel}</span>
            <Meter used={budget.used} total={budget.total} />
            <span className="num">
              {budget.used.toLocaleString("de-DE")} / {budget.total.toLocaleString("de-DE")} Tokens
            </span>
            <span className="poll-hint">{isRunActive ? texte.workspace.pollingHint : texte.workspace.lastUpdated}</span>
          </div>
          {statusQuery.parsed?.agents?.find(a => a.name === "main") && (
            <div className="agent-status-line" style={{ marginTop: 8, fontSize: "0.9rem", color: "var(--c-text-sec)" }}>
              <strong>Status (main):</strong> {statusQuery.parsed.agents.find(a => a.name === "main")?.state}
              {statusQuery.parsed.agents.find(a => a.name === "main")?.note ? ` — ${statusQuery.parsed.agents.find(a => a.name === "main")?.note}` : ""}
            </div>
          )}
          {center}
        </main>
      </div>
      {shareOpen && <ShareDialog workspace={workspace} link={shareLink} onClose={() => setShareOpen(false)} onCopied={() => toast(texte.dialogs.share.copied, texte.dialogs.share.copiedText, true)} />}
      {voucherOpen && <VoucherDialog onClose={() => setVoucherOpen(false)} onSubmit={handleVoucher} />}
    </div>
  );
}
